import { PLC_PATHS } from './pressHMI.schema';

const createField = (path, label, description, options = {}) => ({
  description,
  label,
  path,
  ...options,
});

const createPidFields = (sideLabel, pidPaths, description) => ([
  createField(pidPaths.kp, `${sideLabel} Kp`, description),
  createField(pidPaths.tnSeconds, `${sideLabel} Tn (s)`, description),
  createField(pidPaths.tvSeconds, `${sideLabel} Tv (s)`, description),
  createField(pidPaths.tdSeconds, `${sideLabel} Td (s)`, description),
]);

const createTempFields = (sideLabel, paths) => ([
  createField(
    paths.loading,
    `${sideLabel} Loading Setpoint °C`,
    'Used for standby heating and automation.',
  ),
  createField(paths.forming, `${sideLabel} Forming Setpoint °C`),
  createField(paths.eject, `${sideLabel} Eject Setpoint °C`),
]);

const FORMING_TEMP_FIELDS_BY_SIDE = {
  upper: createTempFields('Upper', {
    loading: PLC_PATHS.cmd.formingRecipe.upperLoadingSetpoint,
    forming: PLC_PATHS.cmd.formingRecipe.upperFormingSetpoint,
    eject: PLC_PATHS.cmd.formingRecipe.upperEjectSetpoint,
  }),
  lower: createTempFields('Lower', {
    loading: PLC_PATHS.cmd.formingRecipe.lowerLoadingSetpoint,
    forming: PLC_PATHS.cmd.formingRecipe.lowerFormingSetpoint,
    eject: PLC_PATHS.cmd.formingRecipe.lowerEjectSetpoint,
  }),
};

const FORMING_SOAK_TIME_FIELDS = [
  createField(PLC_PATHS.cmd.formingRecipe.formingPresoakTime, 'Forming Presoak Time'),
  createField(PLC_PATHS.cmd.formingRecipe.ejectSoakTime, 'Eject Soak Time'),
];

const BONDING_TEMP_FIELDS_BY_SIDE = {
  upper: [
    createField(
      PLC_PATHS.cmd.bondingRecipe.upperLoadingSetpoint,
      'Chip Side Loading Setpoint °C',
      'Used for standby heating and automation.',
    ),
  ],
  lower: [
    createField(
      PLC_PATHS.cmd.bondingRecipe.lowerLoadingSetpoint,
      'Cap Side Loading Setpoint °C',
      'Used for standby heating and automation.',
    ),
  ],
};

const TUNING_TEMP_FIELDS_BY_SIDE = {
  upper: [
    createField(PLC_PATHS.cmd.tuningRecipe.upperZone.heatSetpoint, 'Upper Heat Setpoint °C'),
    createField(PLC_PATHS.cmd.tuningRecipe.upperZone.coolSetpoint, 'Upper Cool Setpoint °C'),
  ],
  lower: [
    createField(PLC_PATHS.cmd.tuningRecipe.lowerZone.heatSetpoint, 'Lower Heat Setpoint °C'),
    createField(PLC_PATHS.cmd.tuningRecipe.lowerZone.coolSetpoint, 'Lower Cool Setpoint °C'),
  ],
};

const TUNING_SEED_PID_FIELDS_BY_SIDE = {
  upper: createPidFields('Upper Seed PID', PLC_PATHS.cmd.tuningRecipe.upperZone.seedPid),
  lower: createPidFields('Lower Seed PID', PLC_PATHS.cmd.tuningRecipe.lowerZone.seedPid),
};

const HEAT_PID_FIELDS_BY_SIDE = {
  upper: createPidFields('Upper Heat PID', PLC_PATHS.cmd.upperHeatPid),
  lower: createPidFields('Lower Heat PID', PLC_PATHS.cmd.lowerHeatPid),
};

const createHeatZoneConfigFields = (paths, labels = {}) => ({
  hold: [
    createField(
      paths.pullDownEntryDelta,
      'Pull-Down Entry Delta °C',
      'Temperature above setpoint that triggers pull-down cooling mode.',
    ),
  ],
  pullDown: [
    createField(
      paths.holdEntryDelta,
      'Hold Entry Delta °C',
      'Temperature below setpoint that triggers hold mode.',
    ),
    createField(
      paths.stableRateThresholdCpm,
      'Stable Rate Threshold °C/min',
      'Maximum heating or cooling rate magnitude considered thermally stable.',
    ),
    createField(
      paths.stableHoldTime,
      'Stable Hold Time (s)',
      'Seconds the stable condition must remain true before pull-down exits.',
    ),
    createField(
      paths.stableOvershootMax,
      'Stable Overshoot Max °C',
      'Maximum overshoot above setpoint still allowed when evaluating pull-down stability. Use 0 to disable.',
    ),
  ],
  safety: [
    createField(
      paths.setpointMin,
      'Setpoint Min °C',
      'Minimum requested setpoint the PLC allows for this zone.',
      { disabled: true },
    ),
    createField(
      paths.setpointMax,
      'Setpoint Max °C',
      'Maximum requested setpoint the PLC allows for this zone.',
      { disabled: true },
    ),
    createField(
      paths.toolTempMinValid,
      'Tool RTD Min Valid °C',
      'Minimum valid tool RTD reading before the zone faults.',
      { disabled: true },
    ),
    createField(
      paths.toolTempMax,
      'Tool RTD Max °C',
      'Critical alarm limit for any individual tool RTD.',
      { disabled: true },
    ),
    createField(
      paths.toolRtdSpreadMax,
      'Tool RTD Spread Max °C',
      'Critical alarm limit for the gap between any tool RTDs in this zone.',
      { disabled: true },
    ),
    createField(
      paths.heaterTempMax,
      labels.heaterTempMax || 'Heater Safety RTD Max °C',
      'Critical alarm limit for the heater tab/body safety RTD.',
      { disabled: true },
    ),
    createField(
      paths.minTempRateAtFullOutputCpm,
      'Warning Heat Rate Min °C/min',
      'Warning threshold while heater output is at full power.',
      { disabled: true },
    ),
    createField(
      paths.faultTempRateAtFullOutputCpm,
      'Fault Heat Rate Min °C/min',
      'Critical alarm threshold while heater output is at full power for 4 seconds.',
      { disabled: true },
    ),
  ],
});

const HEAT_ZONE_CONFIG_FIELDS_BY_SIDE = {
  upper: createHeatZoneConfigFields(PLC_PATHS.cmd.upperHeatZoneConfig, {
    heaterTempMax: 'PEEK RTD Max °C',
  }),
  lower: createHeatZoneConfigFields(PLC_PATHS.cmd.lowerHeatZoneConfig),
};

const createConfigField = (
  key,
  path,
  label,
  section,
  group = null,
  subgroup = null,
) => ({
  key,
  path,
  label,
  section,
  group,
  subgroup,
  type: path.type,
});

const PRESS_HMI_FORMING_CONFIG_FIELDS = [
  createConfigField('formingPresoakTime', PLC_PATHS.cmd.formingRecipe.formingPresoakTime, 'Forming Presoak Time', 'Timing'),
  createConfigField('ejectSoakTime', PLC_PATHS.cmd.formingRecipe.ejectSoakTime, 'Eject Soak Time', 'Timing'),
  createConfigField('upperLoadingSetpoint', PLC_PATHS.cmd.formingRecipe.upperLoadingSetpoint, 'Loading Setpoint °C', 'Recipe Temperatures', 'Upper'),
  createConfigField('upperFormingSetpoint', PLC_PATHS.cmd.formingRecipe.upperFormingSetpoint, 'Forming Setpoint °C', 'Recipe Temperatures', 'Upper'),
  createConfigField('upperEjectSetpoint', PLC_PATHS.cmd.formingRecipe.upperEjectSetpoint, 'Eject Setpoint °C', 'Recipe Temperatures', 'Upper'),
  createConfigField('lowerLoadingSetpoint', PLC_PATHS.cmd.formingRecipe.lowerLoadingSetpoint, 'Loading Setpoint °C', 'Recipe Temperatures', 'Lower'),
  createConfigField('lowerFormingSetpoint', PLC_PATHS.cmd.formingRecipe.lowerFormingSetpoint, 'Forming Setpoint °C', 'Recipe Temperatures', 'Lower'),
  createConfigField('lowerEjectSetpoint', PLC_PATHS.cmd.formingRecipe.lowerEjectSetpoint, 'Eject Setpoint °C', 'Recipe Temperatures', 'Lower'),
  createConfigField('autoVacuum', PLC_PATHS.cmd.formingRecipe.autoVacuum, 'Auto Vacuum', 'Automation'),
  createConfigField('autoIonizer', PLC_PATHS.cmd.formingRecipe.autoIonizer, 'Auto Ionizer', 'Automation'),
];

const PRESS_HMI_BONDING_CONFIG_FIELDS = [
  createConfigField('presoakTime', PLC_PATHS.cmd.bondingRecipe.presoakTime, 'Presoak Time', 'Timing'),
  createConfigField('bondTime', PLC_PATHS.cmd.bondingRecipe.bondTime, 'Bond Time', 'Timing'),
  createConfigField('superheatWaitTime', PLC_PATHS.cmd.bondingRecipe.superheatWaitTime, 'Superheat Wait Time', 'Timing'),
  createConfigField('annealingWaitTime', PLC_PATHS.cmd.bondingRecipe.annealingWaitTime, 'Annealing Wait Time', 'Timing'),
  createConfigField('finalTempWaitTime', PLC_PATHS.cmd.bondingRecipe.finalTempWaitTime, 'Final Temp Wait Time', 'Timing'),
  createConfigField('upperLoadingSetpoint', PLC_PATHS.cmd.bondingRecipe.upperLoadingSetpoint, 'Loading Setpoint °C', 'Recipe Temperatures', 'Chip Side (Upper)'),
  createConfigField('upperBondingSetpoint', PLC_PATHS.cmd.bondingRecipe.upperBondingSetpoint, 'Bonding Setpoint °C', 'Recipe Temperatures', 'Chip Side (Upper)', 'Bonding'),
  createConfigField('upperBondingTolerance', PLC_PATHS.cmd.bondingRecipe.upperBondingTolerance, 'Bonding Tolerance °C', 'Recipe Temperatures', 'Chip Side (Upper)', 'Bonding'),
  createConfigField('upperSuperheatSetpoint', PLC_PATHS.cmd.bondingRecipe.upperSuperheatSetpoint, 'Superheat Setpoint °C', 'Recipe Temperatures', 'Chip Side (Upper)', 'Superheat'),
  createConfigField('upperSuperheatTolerance', PLC_PATHS.cmd.bondingRecipe.upperSuperheatTolerance, 'Superheat Tolerance °C', 'Recipe Temperatures', 'Chip Side (Upper)', 'Superheat'),
  createConfigField('upperAnnealingSetpoint', PLC_PATHS.cmd.bondingRecipe.upperAnnealingSetpoint, 'Annealing Setpoint °C', 'Recipe Temperatures', 'Chip Side (Upper)', 'Annealing'),
  createConfigField('upperAnnealingTolerance', PLC_PATHS.cmd.bondingRecipe.upperAnnealingTolerance, 'Annealing Tolerance °C', 'Recipe Temperatures', 'Chip Side (Upper)', 'Annealing'),
  createConfigField('upperFinalSetpoint', PLC_PATHS.cmd.bondingRecipe.upperFinalSetpoint, 'Final Setpoint °C', 'Recipe Temperatures', 'Chip Side (Upper)'),
  createConfigField('lowerLoadingSetpoint', PLC_PATHS.cmd.bondingRecipe.lowerLoadingSetpoint, 'Loading Setpoint °C', 'Recipe Temperatures', 'Cap Side (Lower)'),
  createConfigField('lowerBondingSetpoint', PLC_PATHS.cmd.bondingRecipe.lowerBondingSetpoint, 'Bonding Setpoint °C', 'Recipe Temperatures', 'Cap Side (Lower)', 'Bonding'),
  createConfigField('lowerBondingTolerance', PLC_PATHS.cmd.bondingRecipe.lowerBondingTolerance, 'Bonding Tolerance °C', 'Recipe Temperatures', 'Cap Side (Lower)', 'Bonding'),
  createConfigField('lowerSuperheatSetpoint', PLC_PATHS.cmd.bondingRecipe.lowerSuperheatSetpoint, 'Superheat Setpoint °C', 'Recipe Temperatures', 'Cap Side (Lower)', 'Superheat'),
  createConfigField('lowerSuperheatTolerance', PLC_PATHS.cmd.bondingRecipe.lowerSuperheatTolerance, 'Superheat Tolerance °C', 'Recipe Temperatures', 'Cap Side (Lower)', 'Superheat'),
  createConfigField('lowerAnnealingSetpoint', PLC_PATHS.cmd.bondingRecipe.lowerAnnealingSetpoint, 'Annealing Setpoint °C', 'Recipe Temperatures', 'Cap Side (Lower)', 'Annealing'),
  createConfigField('lowerAnnealingTolerance', PLC_PATHS.cmd.bondingRecipe.lowerAnnealingTolerance, 'Annealing Tolerance °C', 'Recipe Temperatures', 'Cap Side (Lower)', 'Annealing'),
  createConfigField('lowerFinalSetpoint', PLC_PATHS.cmd.bondingRecipe.lowerFinalSetpoint, 'Final Setpoint °C', 'Recipe Temperatures', 'Cap Side (Lower)'),
  createConfigField('autoIonizer', PLC_PATHS.cmd.bondingRecipe.autoIonizer, 'Auto Ionizer', 'Automation'),
];

export {
  BONDING_TEMP_FIELDS_BY_SIDE,
  PRESS_HMI_FORMING_CONFIG_FIELDS,
  PRESS_HMI_BONDING_CONFIG_FIELDS,
  FORMING_TEMP_FIELDS_BY_SIDE,
  FORMING_SOAK_TIME_FIELDS,
  HEAT_PID_FIELDS_BY_SIDE,
  HEAT_ZONE_CONFIG_FIELDS_BY_SIDE,
  TUNING_SEED_PID_FIELDS_BY_SIDE,
  TUNING_TEMP_FIELDS_BY_SIDE,
};
